import { useState, useEffect } from 'react';
import { agentBankAPI, openAuthenticatedFile } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Building2, CreditCard, FileText, Upload, Trash2, Download, 
  CheckCircle, XCircle, Clock, AlertCircle, Loader2, Eye
} from 'lucide-react';

const DOCUMENT_TYPES = [
  { value: 'contract', label: 'Contract Copy', required: true },
  { value: 'bank_proof', label: 'Bank Account Proof', required: true },
  { value: 'national_id', label: 'National Identity (Passport/ID)', required: true },
  { value: 'company_registration', label: 'Company Registration Certificate', required: true },
  { value: 'accreditation', label: 'Accreditation Certificate', required: false },
  { value: 'british_council', label: 'British Council Certification', required: false },
  { value: 'other', label: 'Other Supporting Document', required: false },
];

export const AgentBankAndDocuments = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [bankDetails, setBankDetails] = useState({
    business_account_name: '',
    business_account_number: '',
    bank_name: '',
    branch_address: '',
    swift_code: '',
    iban_bic_ifsc: '',
    vat_number: ''
  });
  const [bankStatus, setBankStatus] = useState('not_submitted');
  const [bankRejectionReason, setBankRejectionReason] = useState(null);
  
  const [documents, setDocuments] = useState([]);
  const [uploadForm, setUploadForm] = useState({
    document_type: '',
    document_name: '',
    file: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bankRes, docsRes] = await Promise.all([
        agentBankAPI.getBankDetails(),
        agentBankAPI.getDocuments()
      ]);
      
      if (bankRes.data.bank_details) {
        setBankDetails(bankRes.data.bank_details);
      }
      setBankStatus(bankRes.data.bank_status || 'not_submitted');
      setBankRejectionReason(bankRes.data.bank_rejection_reason);
      setDocuments(docsRes.data.documents || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBankDetails = async () => {
    if (!bankDetails.business_account_name || !bankDetails.business_account_number || 
        !bankDetails.bank_name || !bankDetails.branch_address) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      await agentBankAPI.updateBankDetails(bankDetails);
      toast.success('Bank details submitted for approval');
      setBankStatus('pending');
      setBankRejectionReason(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save bank details');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadForm.document_type || !uploadForm.document_name || !uploadForm.file) {
      toast.error('Please select document type, enter name, and choose a file');
      return;
    }
    
    setUploading(true);
    try {
      await agentBankAPI.uploadDocument(
        uploadForm.document_type,
        uploadForm.document_name,
        uploadForm.file
      );
      toast.success('Document uploaded successfully');
      setUploadForm({ document_type: '', document_name: '', file: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await agentBankAPI.deleteDocument(docId);
      toast.success('Document deleted');
      setDocuments(documents.filter(d => d.id !== docId));
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" /> Pending Approval</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700"><AlertCircle className="w-3 h-3 mr-1" /> Not Submitted</Badge>;
    }
  };

  const getDocTypeLabel = (type) => {
    const docType = DOCUMENT_TYPES.find(d => d.value === type);
    return docType ? docType.label : type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Bank Account Details Section */}
      <Card className="border-2 border-teal-200 dark:border-teal-800">
        <CardHeader className="bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-teal-100 dark:bg-teal-900 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <CardTitle className="text-xl">Business Bank Account Details</CardTitle>
                <CardDescription>Required for commission payouts (Business accounts only)</CardDescription>
              </div>
            </div>
            {getStatusBadge(bankStatus)}
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {bankStatus === 'rejected' && bankRejectionReason && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-700 dark:text-red-400 font-medium">Rejection Reason:</p>
              <p className="text-red-600 dark:text-red-300">{bankRejectionReason}</p>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Business Account Name *</Label>
              <Input
                value={bankDetails.business_account_name}
                onChange={(e) => setBankDetails({...bankDetails, business_account_name: e.target.value})}
                placeholder="Company/Business Name on Account"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Business Account Number *</Label>
              <Input
                value={bankDetails.business_account_number}
                onChange={(e) => setBankDetails({...bankDetails, business_account_number: e.target.value})}
                placeholder="Account Number"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Bank Name *</Label>
              <Input
                value={bankDetails.bank_name}
                onChange={(e) => setBankDetails({...bankDetails, bank_name: e.target.value})}
                placeholder="e.g., HSBC, Barclays"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Branch Address *</Label>
              <Input
                value={bankDetails.branch_address}
                onChange={(e) => setBankDetails({...bankDetails, branch_address: e.target.value})}
                placeholder="Bank Branch Address"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">SWIFT Code / Sort Code</Label>
              <Input
                value={bankDetails.swift_code}
                onChange={(e) => setBankDetails({...bankDetails, swift_code: e.target.value})}
                placeholder="SWIFT/BIC or Sort Code"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">IBAN / BIC / IFSC Code</Label>
              <Input
                value={bankDetails.iban_bic_ifsc}
                onChange={(e) => setBankDetails({...bankDetails, iban_bic_ifsc: e.target.value})}
                placeholder="International Banking Code"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">VAT Number (Optional)</Label>
              <Input
                value={bankDetails.vat_number}
                onChange={(e) => setBankDetails({...bankDetails, vat_number: e.target.value})}
                placeholder="VAT Registration Number"
                className="mt-1"
              />
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-amber-800 dark:text-amber-200 text-sm">
              <strong>Note:</strong> Only business bank accounts are accepted for commission payouts. 
              Personal accounts will be rejected.
            </p>
          </div>
          
          <div className="mt-6 flex justify-end">
            <Button 
              onClick={handleSaveBankDetails}
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
              {bankStatus === 'not_submitted' ? 'Submit Bank Details' : 'Update Bank Details'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Compliance Documents Section */}
      <Card className="border-2 border-blue-200 dark:border-blue-800">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-xl">Compliance Documents</CardTitle>
              <CardDescription>Upload required documents for verification</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {/* Required Documents Checklist */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-3">
            {DOCUMENT_TYPES.filter(d => d.required).map(docType => {
              const uploaded = documents.find(d => d.document_type === docType.value);
              return (
                <div 
                  key={docType.value}
                  className={`p-3 rounded-lg border ${uploaded ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
                >
                  <div className="flex items-center gap-2">
                    {uploaded ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-gray-400" />
                    )}
                    <span className={`text-sm ${uploaded ? 'text-green-700' : 'text-gray-600'}`}>
                      {docType.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Upload Form */}
          <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-lg mb-6">
            <h4 className="font-medium mb-4">Upload New Document</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm">Document Type</Label>
                <Select 
                  value={uploadForm.document_type} 
                  onValueChange={(v) => setUploadForm({...uploadForm, document_type: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label} {type.required && '*'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Document Name</Label>
                <Input
                  value={uploadForm.document_name}
                  onChange={(e) => setUploadForm({...uploadForm, document_name: e.target.value})}
                  placeholder="e.g., Passport Copy"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">File (PDF, JPG, PNG, DOC)</Label>
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => setUploadForm({...uploadForm, file: e.target.files[0]})}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button 
                onClick={handleUploadDocument}
                disabled={uploading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload Document
              </Button>
            </div>
          </div>

          {/* Uploaded Documents List */}
          {documents.length > 0 ? (
            <div className="space-y-3">
              <h4 className="font-medium">Uploaded Documents</h4>
              {documents.map(doc => (
                <div 
                  key={doc.id}
                  className="flex items-center justify-between p-3 bg-white dark:bg-slate-700 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-blue-500" />
                    <div>
                      <p className="font-medium text-sm">{doc.document_name}</p>
                      <p className="text-xs text-gray-500">{getDocTypeLabel(doc.document_type)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => openAuthenticatedFile(doc.file_url).catch(() => toast.error('Failed to open document'))}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded cursor-pointer"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteDocument(doc.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No documents uploaded yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AgentBankAndDocuments;
